import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Header from "units/dashboard/components/Header";
import { SidebarProvider } from "units/dashboard/context/SidebarProvider";
import AdminSidebar from "../components/AdminSidebar";

import CreateCriterionForm from "./components/CreateCriterionForm";
import CriteriaListSection from "./components/CriteriaListSection";
import EditCriterionModal from "./components/EditCriterionModal";
import ConfirmationDialog from "./components/ConfirmationDialog";

import {
    getCriteria,
    updateCriterionDescription,
    updateCriterionPrompt,
    updateCriterionRule,
    updateCriterionVisibility,
    reactivateCriterion,
    deactivateCriterion,
    deleteCriterion,
} from "./services/adminSellFunnel";

const CreateFunnelCriteria = () => {
    const accessToken = useSelector((state) => state.auth?.accessToken);

    // Criteria List
    const [criteriaData, setCriteriaData] = useState([]);
    const [criteriaLoading, setCriteriaLoading] = useState(true);
    const [criteriaError, setCriteriaError] = useState("");

    // Edit Modal
    const [editingCriterion, setEditingCriterion] = useState(null);
    const [editTitle, setEditTitle] = useState("");
    const [editDescription, setEditDescription] = useState("");
    const [editStageOrder, setEditStageOrder] = useState(1);
    const [editPrompt, setEditPrompt] = useState("");
    const [editConditions, setEditConditions] = useState([
        {
            field: "messageCount",
            operator: "<",
            value: "",
        },
    ]);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    // Confirmation Dialog
    const [confirmationDialog, setConfirmationDialog] = useState({
        isOpen: false,
        title: "",
        message: "",
        confirmText: "",
        onConfirm: null,
        isDanger: false,
    });

    // ============================
    // Load Criteria
    // ============================
    const loadCriteria = async () => {
        if (!accessToken) {
            setCriteriaLoading(false);
            return;
        }

        try {
            setCriteriaLoading(true);
            setCriteriaError("");
            const response = await getCriteria(accessToken);
            setCriteriaData(Array.isArray(response) ? response : []);
        } catch (err) {
            console.error("GET CRITERIA ERROR:", err);
            const backendMessage =
                err?.response?.data?.message ||
                err?.response?.data?.detail ||
                err?.response?.data?.error ||
                "دریافت لیست معیارها با خطا مواجه شد.";
            setCriteriaError(backendMessage);
        } finally {
            setCriteriaLoading(false);
        }
    };

    useEffect(() => {
        loadCriteria();
    }, [accessToken]);

    // ============================
    // Helpers
    // ============================
    const getApiStatus = (item) => {
        if (!item?.isActive) return "inactive";
        if (!item?.isVisible) return "hidden";
        return "active";
    };

    const getTypeBadge = (itemType) => {
        if (itemType === "AI") {
            return (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-50 px-3 py-1 text-xs font-medium text-teal-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-teal-600" />
                    AI
                </span>
            );
        }
        if (itemType === "SYSTEM_RULE") {
            return (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                    SYSTEM_RULE
                </span>
            );
        }
        return null;
    };

    const getStatusBadge = (status) => {
        if (status === "active") {
            return (
                <span className="inline-flex rounded-full bg-teal-50 px-3 py-1 text-xs font-medium text-teal-700">
                    فعال / نمایان
                </span>
            );
        }
        if (status === "hidden") {
            return (
                <span className="inline-flex rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
                    مخفی‌شده
                </span>
            );
        }
        return (
            <span className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-500">
                غیرفعال
            </span>
        );
    };

    // ============================
    // Edit Modal Handlers
    // ============================
    const openEditModal = (criterion) => {
        const safeCriterion = {
            ...criterion,
            usage: criterion.usage || {
                templateUsageCount: 0,
                personalizedFunnelUsageCount: 0,
                totalUsageCount: 0,
            },
        };

        setEditingCriterion(safeCriterion);
        setEditTitle(safeCriterion.title);
        setEditDescription(safeCriterion.description || "");
        setEditStageOrder(safeCriterion.stageOrder);

        if (safeCriterion.type === "AI") {
            setEditPrompt(safeCriterion.prompt || "");
        } else {
            const config = safeCriterion.ruleConfig || {};

            // فرمت جدید چند شرطی
            if (Array.isArray(config.conditions) && config.conditions.length > 0) {
                setEditConditions(
                    config.conditions.map((condition) => ({
                        field: condition.field || "messageCount",
                        operator: condition.operator || "<",
                        value:
                            condition.value !== undefined
                                ? String(condition.value)
                                : "",
                    }))
                );
            } else {
                // سازگاری با معیارهای قدیمی تک‌شرطی
                setEditConditions([
                    {
                        field: config.field || "messageCount",
                        operator: config.operator || "<",
                        value:
                            config.value !== undefined
                                ? String(config.value)
                                : "",
                    },
                ]);
            }
        }

        setIsEditModalOpen(true);
    };

    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setEditingCriterion(null);
        setError("");
        setSuccessMessage("");
    };

    const handleUpdateDescription = async () => {
        if (!editingCriterion) return;
        setError("");
        setSuccessMessage("");

        const data = {};
        if (editTitle !== editingCriterion.title) data.title = editTitle;
        if (editDescription !== editingCriterion.description) data.description = editDescription;
        if (editStageOrder !== editingCriterion.stageOrder) data.stageOrder = editStageOrder;

        if (Object.keys(data).length === 0) {
            setError("هیچ تغییری اعمال نشده است.");
            return;
        }

        try {
            setIsUpdating(true);
            await updateCriterionDescription(editingCriterion.id, data, accessToken);
            setSuccessMessage("معیار با موفقیت به‌روزرسانی شد.");
            await loadCriteria();
            closeEditModal();
        } catch (err) {
            console.error("UPDATE DESCRIPTION ERROR:", err);
            if (err.response?.status === 409) {
                const usage = err.response?.data?.usage;
                setError(
                    `این معیار در ${usage?.templateUsageCount || 0} قیف آماده و ${usage?.personalizedFunnelUsageCount || 0} قیف کسب‌وکار استفاده شده است.`
                );
            } else {
                const backendMessage =
                    err?.response?.data?.message ||
                    err?.response?.data?.detail ||
                    err?.response?.data?.error ||
                    "به‌روزرسانی با خطا مواجه شد.";
                setError(backendMessage);
            }
        } finally {
            setIsUpdating(false);
        }
    };

    const handleUpdatePrompt = async () => {
        if (!editingCriterion || editingCriterion.type !== "AI") return;

        if (!editPrompt.trim()) {
            setError("Prompt را وارد کنید.");
            return;
        }

        const usage = editingCriterion.usage || {};
        const affectedCount = usage.personalizedFunnelUsageCount || 0;

        if (affectedCount > 0) {
            setConfirmationDialog({
                isOpen: true,
                title: "تأیید تغییر Prompt",
                message: `این تغییر باعث تحلیل مجدد ${affectedCount} کسب‌وکار می‌شود. ادامه می‌دهید؟`,
                confirmText: "بله، ادامه بده",
                onConfirm: async () => {
                    setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));
                    await updatePromptApi();
                },
                isDanger: true,
            });
        } else {
            await updatePromptApi();
        }
    };

    const updatePromptApi = async () => {
        try {
            setIsUpdating(true);
            setError("");
            await updateCriterionPrompt(
                editingCriterion.id,
                editPrompt,
                editTitle,
                editDescription,
                accessToken
            );
            setSuccessMessage("Prompt با موفقیت به‌روزرسانی شد.");
            await loadCriteria();
            closeEditModal();
        } catch (err) {
            console.error("UPDATE PROMPT ERROR:", err);
            const backendMessage =
                err?.response?.data?.message ||
                err?.response?.data?.detail ||
                err?.response?.data?.error ||
                "به‌روزرسانی Prompt با خطا مواجه شد.";
            setError(backendMessage);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleUpdateRule = async () => {
        if (!editingCriterion || editingCriterion.type !== "SYSTEM_RULE") {
            return;
        }

        setError("");
        setSuccessMessage("");

        // اعتبارسنجی شرط‌ها
        if (!Array.isArray(editConditions) || editConditions.length === 0) {
            setError("حداقل یک شرط باید وجود داشته باشد.");
            return;
        }

        const invalidConditionIndex = editConditions.findIndex(
            (condition) =>
                !condition.field ||
                !condition.operator ||
                condition.value === "" ||
                condition.value === null ||
                condition.value === undefined
        );

        if (invalidConditionIndex !== -1) {
            setError(
                `مقدار شرط ${invalidConditionIndex + 1} را وارد کنید.`
            );
            return;
        }

        // فقط عدد صحیح غیرمنفی
        const hasInvalidValue = editConditions.some(
            (condition) =>
                !/^\d+$/.test(String(condition.value))
        );

        if (hasInvalidValue) {
            setError("مقدار تمام شرط‌ها باید عدد صحیح و غیرمنفی باشد.");
            return;
        }

        const ruleConfig = {
            conditions: editConditions.map((condition) => ({
                field: condition.field,
                operator: condition.operator,
                value: Number(condition.value),
            })),
        };

        const usage = editingCriterion.usage || {};
        const affectedCount = usage.personalizedFunnelUsageCount || 0;

        if (affectedCount > 0) {
            setConfirmationDialog({
                isOpen: true,
                title: "تأیید تغییر قوانین",
                message: `این تغییر باعث تحلیل مجدد ${affectedCount} کسب‌وکار می‌شود. ادامه می‌دهید؟`,
                confirmText: "بله، ادامه بده",
                onConfirm: async () => {
                    setConfirmationDialog((prev) => ({
                        ...prev,
                        isOpen: false,
                    }));

                    await updateRuleApi(ruleConfig);
                },
                isDanger: true,
            });
        } else {
            await updateRuleApi(ruleConfig);
        }
    };

    const updateRuleApi = async (ruleConfig) => {
        try {
            setIsUpdating(true);
            setError("");

            await updateCriterionRule(
                editingCriterion.id,
                ruleConfig,
                editTitle,
                editDescription,
                accessToken
            );

            setSuccessMessage("قانون با موفقیت به‌روزرسانی شد.");
            await loadCriteria();
            closeEditModal();
        } catch (err) {
            const backendMessage =
                err?.response?.data?.message ||
                err?.response?.data?.detail ||
                err?.response?.data?.error ||
                "به‌روزرسانی قانون با خطا مواجه شد.";
            setError(backendMessage);
        } finally {
            setIsUpdating(false);
        }
    };

    // ============================
    // Actions
    // ============================
    const handleToggleVisibility = async (criterion) => {
        try {
            const newVisibility = !criterion.isVisible;
            await updateCriterionVisibility(criterion.id, newVisibility, accessToken);
            await loadCriteria();
        } catch (err) {
            console.error("TOGGLE VISIBILITY ERROR:", err);
            const backendMessage =
                err?.response?.data?.message ||
                err?.response?.data?.detail ||
                err?.response?.data?.error ||
                "تغییر وضعیت نمایش با خطا مواجه شد.";
            alert(backendMessage);
        }
    };

    const handleDeactivate = async (criterion) => {
        const usage = criterion.usage || {};
        const totalUsage = usage.totalUsageCount || 0;

        if (totalUsage > 0) {
            alert(
                `این معیار در ${usage.templateUsageCount || 0} قیف آماده و ${usage.personalizedFunnelUsageCount || 0} قیف کسب‌وکار استفاده شده است.`
            );
            return;
        }

        setConfirmationDialog({
            isOpen: true,
            title: "غیرفعال‌سازی معیار",
            message: `آیا از غیرفعال‌سازی "${criterion.title}" اطمینان دارید؟`,
            confirmText: "بله، غیرفعال کن",
            onConfirm: async () => {
                setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));
                try {
                    await deactivateCriterion(criterion.id, accessToken);
                    await loadCriteria();
                } catch (err) {
                    console.error("DEACTIVATE ERROR:", err);
                    const backendMessage =
                        err?.response?.data?.message ||
                        err?.response?.data?.detail ||
                        err?.response?.data?.error ||
                        "غیرفعال‌سازی با خطا مواجه شد.";
                    alert(backendMessage);
                }
            },
            isDanger: true,
        });
    };

    const handleReactivate = async (criterion) => {
        setConfirmationDialog({
            isOpen: true,
            title: "فعال‌سازی مجدد معیار",
            message: `آیا از فعال‌سازی مجدد "${criterion.title}" اطمینان دارید؟`,
            confirmText: "بله، فعال کن",
            onConfirm: async () => {
                setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));
                try {
                    setIsUpdating(true);
                    setError("");
                    await reactivateCriterion(criterion.id, accessToken);
                    setSuccessMessage("✅ معیار با موفقیت فعال شد.");
                    await loadCriteria();
                } catch (err) {
                    console.error("REACTIVATE ERROR:", err);
                    const backendMessage =
                        err?.response?.data?.message ||
                        err?.response?.data?.detail ||
                        err?.response?.data?.error ||
                        "فعال‌سازی مجدد با خطا مواجه شد.";
                    setError(`❌ ${backendMessage}`);
                    alert(`❌ ${backendMessage}`);
                } finally {
                    setIsUpdating(false);
                }
            },
            isDanger: false,
        });
    };

    const handleDelete = async (criterion) => {
        setConfirmationDialog({
            isOpen: true,
            title: "حذف قطعی معیار",
            message: `آیا از حذف "${criterion.title}" اطمینان دارید؟ این عمل قابل برگشت نیست.`,
            confirmText: "بله، حذف کن",
            onConfirm: async () => {
                setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));
                try {
                    await deleteCriterion(criterion.id, accessToken);
                    await loadCriteria();
                } catch (err) {
                    console.error("DELETE ERROR:", err);
                    const backendMessage =
                        err?.response?.data?.message ||
                        err?.response?.data?.detail ||
                        err?.response?.data?.error ||
                        "حذف با خطا مواجه شد.";
                    alert(backendMessage);
                }
            },
            isDanger: true,
        });
    };

    return (
        <SidebarProvider>
            <nav className="fixed left-0 right-0 top-0 z-40 flex items-center border-b border-teal-100 bg-white print:hidden dark:border-zinc-700 dark:bg-zinc-800">
                <Header />
            </nav>

            <div className="vertical-menu rtl:right-0 ltr:left-0 fixed bottom-0 top-16 z-30 h-[calc(100vh-4rem)] border-r border-teal-100 bg-white print:hidden dark:border-neutral-700 dark:bg-zinc-800">
                <AdminSidebar />
            </div>

            <main
                dir="rtl"
                className="min-h-screen overflow-x-hidden bg-[#F8FAFA] px-4 pb-8 pt-20 sm:px-6 lg:mr-[250px] lg:px-8"
            >
                <div className="mx-auto w-full max-w-[1100px]">
                    {/* Page Header */}
                    <div className="mb-7">
                        <div className="mb-2 flex items-center gap-2">
                            <div className="h-8 w-1 rounded-full bg-[#D4A017]" />
                            <h1 className="text-2xl font-bold text-[#173B3A]">
                                مدیریت معیارها
                            </h1>
                        </div>
                        <p className="max-w-3xl text-sm leading-7 text-gray-500">
                            هر معیار دقیقاً برای یک دسته (۱ تا ۶) تعریف می‌شود و مدیر کسب‌وکار
                            وقتی دارد دسته‌ی مربوطه را شخصی‌سازی می‌کند، فقط معیارهای همان دسته را می‌بیند.
                        </p>
                    </div>

                    {/* Create Form */}
                    <CreateCriterionForm
                        accessToken={accessToken}
                        onCreated={loadCriteria}
                    />

                    {/* Criteria List */}
                    <CriteriaListSection
                        criteriaData={criteriaData}
                        criteriaLoading={criteriaLoading}
                        criteriaError={criteriaError}
                        getApiStatus={getApiStatus}
                        getTypeBadge={getTypeBadge}
                        getStatusBadge={getStatusBadge}
                        onEdit={openEditModal}
                        onToggleVisibility={handleToggleVisibility}
                        onDeactivate={handleDeactivate}
                        onReactivate={handleReactivate}
                        onDelete={handleDelete}
                    />
                </div>
            </main>

            {/* Edit Modal */}
            <EditCriterionModal
                isOpen={isEditModalOpen}
                onClose={closeEditModal}
                criterion={editingCriterion}

                title={editTitle}
                setTitle={setEditTitle}

                description={editDescription}
                setDescription={setEditDescription}

                stageOrder={editStageOrder}
                setStageOrder={setEditStageOrder}

                prompt={editPrompt}
                setPrompt={setEditPrompt}

                conditions={editConditions}
                setConditions={setEditConditions}

                onUpdateDescription={handleUpdateDescription}
                onUpdatePrompt={handleUpdatePrompt}
                onUpdateRule={handleUpdateRule}

                isUpdating={isUpdating}
                error={error}
                successMessage={successMessage}
            />

            {/* Confirmation Dialog */}
            <ConfirmationDialog
                isOpen={confirmationDialog.isOpen}
                onClose={() =>
                    setConfirmationDialog((prev) => ({ ...prev, isOpen: false }))
                }
                onConfirm={confirmationDialog.onConfirm}
                title={confirmationDialog.title}
                message={confirmationDialog.message}
                confirmText={confirmationDialog.confirmText}
                isDanger={confirmationDialog.isDanger}
            />
        </SidebarProvider>
    );
};

export default CreateFunnelCriteria;